/**
 * Lead Broadcast Service
 *
 * API service for corporate lead offer broadcasting and claiming.
 * Handles broadcasting offers to company, claiming, approving/rejecting claims.
 *
 * Routes:
 *   /api/leads/{leadId}/broadcast       — create, show, cancel, claims
 *   /api/broadcasts/available           — employee dashboard
 *   /api/broadcasts/{id}/lead-data      — masked lead data
 *   /api/broadcasts/{id}/claim          — claim an offer
 *   /api/claims/received|sent              — agent/employee dashboards
 *   /api/claims/{id}/approve|reject|withdraw
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS
// ================================

export const BROADCAST_STATUS = {
    active: { label: 'Active', severity: 'success', icon: 'pi-megaphone' },
    processor_selected: { label: 'Processor Selected', severity: 'info', icon: 'pi-check-circle' },
    cancelled: { label: 'Cancelled', severity: 'danger', icon: 'pi-times-circle' },
    completed: { label: 'Completed', severity: 'success', icon: 'pi-check-circle' },
    exhausted: { label: 'Exhausted', severity: 'danger', icon: 'pi-times-circle' },
};

export const CLAIM_STATUS = {
    pending: { label: 'Pending', severity: 'warning', icon: 'pi-clock' },
    approved: { label: 'Approved', severity: 'success', icon: 'pi-check-circle' },
    rejected: { label: 'Rejected', severity: 'danger', icon: 'pi-times-circle' },
    withdrawn: { label: 'Withdrawn', severity: 'secondary', icon: 'pi-undo' },
    declined: { label: 'Declined', severity: 'secondary', icon: 'pi-minus-circle' },
};

// ================================
// API SERVICE
// ================================

export const leadBroadcastService = {

    // ── Broadcast Endpoints ──

    /**
     * Create a broadcast — snapshot offers and make visible to company
     * @param {number} leadId
     * @returns {Promise<Object>}
     */
    broadcastOffers: async (leadId) => {
        try {
            const response = await axiosInstance.post(`/leads/${leadId}/broadcast`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.broadcastOffers:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get all active broadcasts for employee's company
     * @param {Object} filters - { page, limit, status, search, date_from, date_to }
     * @returns {Promise<Object>}
     */
    getAvailableBroadcasts: async (filters = {}) => {
        try {
            const params = new URLSearchParams();
            if (filters.page)      params.append('page', filters.page);
            if (filters.limit)     params.append('limit', filters.limit);
            if (filters.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters.search)    params.append('search', filters.search);
            if (filters.date_from)    params.append('date_from', filters.date_from);
            if (filters.date_to)      params.append('date_to', filters.date_to);
            if (filters.loan_type_id) params.append('loan_type_id', filters.loan_type_id);

            const query = params.toString();
            const url = query ? `/broadcasts/available?${query}` : '/broadcasts/available';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getAvailableBroadcasts:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get broadcast details for a specific lead
     * @param {number} leadId
     * @returns {Promise<Object>}
     */
    getBroadcastForLead: async (leadId) => {
        try {
            const response = await axiosInstance.get(`/leads/${leadId}/broadcast`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getBroadcastForLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get masked/full lead data for a broadcast
     * @param {number} broadcastId
     * @returns {Promise<Object>}
     */
    getBroadcastLeadData: async (broadcastId) => {
        try {
            const response = await axiosInstance.get(`/broadcasts/${broadcastId}/lead-data`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getBroadcastLeadData:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Fetch fresh signed payslip URLs for a broadcast (on-demand).
     *
     * Backend generates 1-hour signed S3 URLs on every call (never returns a
     * cached URL) and writes an audit log entry recording the view. Returns
     * { success, data: [{url, name}, ...] } on success.
     *
     * Available to all company members — lenders need to verify payslip
     * authenticity before claiming.
     *
     * @param {number} broadcastId
     * @returns {Promise<Object>}
     */
    getBroadcastPayslipUrls: async (broadcastId) => {
        try {
            const response = await axiosInstance.get(`/broadcasts/${broadcastId}/payslip-urls`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getBroadcastPayslipUrls:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Edit a single offer inside a broadcast. Sends the full edited offer
     * object; the backend whitelists which fields are actually mutable
     * (sensitive identifiers like bank_id / lender_id are ignored). The
     * server stamps updated_by_user_id / updated_by_email / updated_at on
     * the offer so the UI can show "Updated by ..." inline.
     *
     * Returns { success, data: { offer, best_rate, best_emi, changed_fields } }.
     *
     * @param {number} broadcastId
     * @param {string} offerId  The offer.offer_id (or offer.id for legacy data)
     * @param {Object} offer    The full edited offer object
     * @returns {Promise<Object>}
     */
    updateBroadcastOffer: async (broadcastId, offerId, offer) => {
        try {
            const response = await axiosInstance.patch(
                `/broadcasts/${broadcastId}/offers/${encodeURIComponent(offerId)}`,
                { offer }
            );
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.updateBroadcastOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Toggle the verified flag on a single offer inside a broadcast.
     * @param {number} broadcastId
     * @param {string} offerId
     * @param {boolean} verified  true to mark verified, false to clear
     * @returns {Promise<Object>}
     */
    verifyBroadcastOffer: async (broadcastId, offerId, verified) => {
        try {
            const response = await axiosInstance.patch(
                `/broadcasts/${broadcastId}/offers/${encodeURIComponent(offerId)}/verify`,
                { verified }
            );
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.verifyBroadcastOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Cancel a broadcast and auto-reject all pending claims
     * @param {number} leadId
     * @param {string} [reason]
     * @returns {Promise<Object>}
     */
    cancelBroadcast: async (leadId, reason = null) => {
        try {
            const payload = {};
            if (reason) payload.reason = reason;

            const response = await axiosInstance.post(`/leads/${leadId}/broadcast/cancel`, payload);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.cancelBroadcast:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Pre-check: does this lead have an active broadcast, and how many pending
     * claims would be auto-rejected if the lead owner regenerates offers?
     * Used by the wizard's Regenerate confirmation dialog to decide whether
     * to show the "this will displace teammate claims" warning.
     * @param {number} leadId
     * @returns {Promise<{success: boolean, data: {has_active_broadcast: boolean, broadcast_id: number|null, pending_claims_count: number, pending_claimants: Array}}>}
     */
    getBroadcastClaimSummary: async (leadId) => {
        try {
            const response = await axiosInstance.get(`/leads/${leadId}/broadcast/claim-summary`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getBroadcastClaimSummary:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Atomic cancel-old + create-new broadcast in one backend transaction.
     * Called by the wizard AFTER a successful regenerate. The previous active
     * broadcast (if any) is cancelled with its pending claims auto-rejected
     * with reason "Offers regenerated by lead owner", then a new broadcast
     * is created from the lead's latest offer generation.
     * @param {number} leadId
     * @returns {Promise<{success: boolean, message: string, data: {new_broadcast: Object|null, cancelled_broadcast_id: number|null, rejected_claims_count: number, rejected_claimants: Array}}>}
     */
    replaceBroadcastAfterRegenerate: async (leadId) => {
        try {
            const response = await axiosInstance.post(`/leads/${leadId}/broadcast/replace-after-regenerate`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.replaceBroadcastAfterRegenerate:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Per-lead Generation History — every LeadOfferGeneration row with its
     * associated broadcast(s) and claim summary. Used by the View Leads
     * page's collapsible history panel. Lead owner + company admin only.
     * @param {number} leadId
     * @returns {Promise<{success: boolean, data: {lead_id: number, total_generations: number, generations: Array}}>}
     */
    getLeadGenerationHistory: async (leadId) => {
        try {
            const response = await axiosInstance.get(`/leads/${leadId}/broadcast/generation-history`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getLeadGenerationHistory:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Fetch up to 10 unseen claim-rejection notifications for the current user.
     * Used by ClaimRejectionNotifier to surface one-time toasts on next page
     * load after a teammate's claim was auto-rejected.
     * @returns {Promise<{success: boolean, data: {unseen_count: number, notifications: Array}}>}
     */
    getUnseenClaimRejections: async () => {
        try {
            const response = await axiosInstance.get('/notifications/claim-rejections/unseen');
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getUnseenClaimRejections:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Stamp seen_at on a list of claim-rejection notification IDs. Idempotent.
     * Frontend calls this right after rendering the toasts so they don't show
     * again on the next page load.
     * @param {number[]} notificationIds
     */
    markClaimRejectionsSeen: async (notificationIds) => {
        try {
            const response = await axiosInstance.post('/notifications/claim-rejections/mark-seen', {
                notification_ids: notificationIds,
            });
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.markClaimRejectionsSeen:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Paginated list of every claim-rejection notification for the current
     * user (seen + unseen). Powers the dedicated Notifications page in the
     * sidebar.
     * @param {Object} filters { page, limit, only_unseen }
     */
    listClaimRejections: async (filters = {}) => {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.set('page', filters.page);
            if (filters.limit) params.set('limit', filters.limit);
            if (filters.only_unseen) params.set('only_unseen', '1');
            const qs = params.toString();
            const response = await axiosInstance.get(
                `/notifications/claim-rejections${qs ? `?${qs}` : ''}`
            );
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.listClaimRejections:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Bulk mark every unseen notification as seen. Used by the "Mark all as
     * read" button on the notifications page.
     */
    markAllClaimRejectionsSeen: async () => {
        try {
            const response = await axiosInstance.post('/notifications/claim-rejections/mark-all-seen');
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.markAllClaimRejectionsSeen:', error);
            throw error.response?.data || error;
        }
    },

    // ── Claim Endpoints ──

    /**
     * Claim an offer from a broadcast
     * @param {number} broadcastId
     * @param {string} offerId
     * @param {string} [message]
     * @returns {Promise<Object>}
     */
    claimOffer: async (broadcastId, offerId, message = null) => {
        try {
            const payload = { offer_id: offerId };
            if (message) payload.message = message;

            const response = await axiosInstance.post(`/broadcasts/${broadcastId}/claim`, payload);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.claimOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get all claims for a lead's broadcast
     * @param {number} leadId
     * @returns {Promise<Object>}
     */
    getClaimsForLead: async (leadId) => {
        try {
            const response = await axiosInstance.get(`/leads/${leadId}/broadcast/claims`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getClaimsForLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Directly assign any employee as processor (no pending claim required)
     * @param {number} broadcastId
     * @param {number} userId   - The employee to assign
     * @param {string} offerId  - The offer to assign them to
     * @returns {Promise<Object>}
     */
    assignProcessor: async (broadcastId, userId, offerId) => {
        try {
            const response = await axiosInstance.post(`/broadcasts/${broadcastId}/assign-processor`, {
                user_id: userId,
                offer_id: offerId,
            });
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.assignProcessor:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Approve a claim — select processor
     * @param {number} claimId
     * @returns {Promise<Object>}
     */
    approveClaim: async (claimId) => {
        try {
            const response = await axiosInstance.post(`/claims/${claimId}/approve`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.approveClaim:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Reject a claim
     * @param {number} claimId
     * @param {string} [reason]
     * @returns {Promise<Object>}
     */
    rejectClaim: async (claimId, reason = null) => {
        try {
            const payload = {};
            if (reason) payload.reason = reason;

            const response = await axiosInstance.post(`/claims/${claimId}/reject`, payload);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.rejectClaim:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get all received claims across agent's broadcast leads
     * @param {Object} filters - { page, limit, status, search, date_from, date_to }
     * @returns {Promise<Object>}
     */
    getReceivedClaims: async (filters = {}) => {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append('page', filters.page);
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.loan_type_id) params.append('loan_type_id', filters.loan_type_id);

            const query = params.toString();
            const url = query ? `/claims/received?${query}` : '/claims/received';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getReceivedClaims:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get all claims sent by the current employee
     * @param {Object} filters - { page, limit, status, search, date_from, date_to }
     * @returns {Promise<Object>}
     */
    getSentClaims: async (filters = {}) => {
        try {
            const params = new URLSearchParams();
            if (filters.page) params.append('page', filters.page);
            if (filters.limit) params.append('limit', filters.limit);
            if (filters.status && filters.status !== 'all') params.append('status', filters.status);
            if (filters.search) params.append('search', filters.search);
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.loan_type_id) params.append('loan_type_id', filters.loan_type_id);

            const query = params.toString();
            const url = query ? `/claims/sent?${query}` : '/claims/sent';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getSentClaims:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Withdraw own claim
     * @param {number} claimId
     * @returns {Promise<Object>}
     */
    withdrawClaim: async (claimId) => {
        try {
            const response = await axiosInstance.post(`/claims/${claimId}/withdraw`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.withdrawClaim:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Regenerate offers for an existing broadcast (smart merge)
     * @param {number} broadcastId
     * @param {string|null} generationId - optional specific generation to use
     * @returns {Promise<Object>}
     */
    regenerateOffers: async (broadcastId, { generationId = null, offerParams = null } = {}) => {
        try {
            const payload = { generation_id: generationId };
            if (offerParams) {
                Object.assign(payload, offerParams);
            }
            const response = await axiosInstance.post(`/broadcasts/${broadcastId}/regenerate-offers`, payload);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.regenerateOffers:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Preview offer regeneration — generates offers without saving.
     * Returns offers + merge preview stats for employee review.
     */
    previewRegenerateOffers: async (broadcastId, offerParams) => {
        try {
            const payload = { ...offerParams, preview: true };
            const response = await axiosInstance.post(`/broadcasts/${broadcastId}/regenerate-offers`, payload);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.previewRegenerateOffers:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get offer generation params for a broadcast's lead (no PII).
     * Used to pre-fill the regenerate offers form.
     */
    getOfferParams: async (broadcastId) => {
        try {
            const response = await axiosInstance.get(`/broadcasts/${broadcastId}/offer-params`);
            return response.data;
        } catch (error) {
            console.error('leadBroadcastService.getOfferParams:', error);
            throw error.response?.data || error;
        }
    },
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get broadcast status config
 * @param {string} status
 * @returns {Object}
 */
export const getBroadcastStatusConfig = (status) => {
    return BROADCAST_STATUS[status] || { label: status || 'Unknown', severity: 'secondary', icon: 'pi-circle' };
};

/**
 * Get claim status config
 * @param {string} status
 * @returns {Object}
 */
export const getClaimStatusConfig = (status) => {
    return CLAIM_STATUS[status] || { label: status || 'Unknown', severity: 'secondary', icon: 'pi-circle' };
};

/**
 * Format currency in Indian format
 * @param {number} amount
 * @returns {string}
 */
export const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(amount);
};

/**
 * Format loan amount with Lakhs/Crores
 * @param {number} amount
 * @returns {string}
 */
export const formatLoanAmount = (amount) => {
    if (!amount) return '-';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount}`;
};

export default leadBroadcastService;
